// src/components/module/almacen/InventariosDataTable/InventariosDataTable.tsx
import React, { useContext } from "react";
import { DataGrid, GridColDef, GridToolbar, esES } from "@mui/x-data-grid";
import { Box, Button, Paper, Typography, Stack, Chip } from "@mui/material";
import { Add as AddIcon, CloudUpload as ImportIcon, WarningAmber as WarningIcon } from "@mui/icons-material";
import { InventarioItem } from "../../../../services/api/inventariosApi";
import { DarkModeContext } from "../../../../context/darkModeContext";

const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0", accent: "#2383E2", accentBg: "#E8F0FE", hover: "rgba(55,53,47,0.04)", shadow: "0 1px 3px rgba(0,0,0,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444", accent: "#529CCA", accentBg: "#143A52", hover: "rgba(255,255,255,0.04)", shadow: "0 1px 3px rgba(0,0,0,0.2)" },
};

interface InventariosDataTableProps {
  items: InventarioItem[]; columns: GridColDef[];
  onEdit: (item: InventarioItem) => void; onDelete: (id: number) => void;
  onAdd: () => void; onImport: () => void; isLoading: boolean;
  stockBajoCount?: number;
}

const InventariosDataTable: React.FC<InventariosDataTableProps> = ({
  items, columns, onAdd, onImport, isLoading, stockBajoCount = 0,
}) => {
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${p.border}`, borderRadius: "12px", overflow: "hidden", bgcolor: p.bg, boxShadow: p.shadow }}>
        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 0, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: "0.82rem", color: p.textT }}>{items.length} registros</Typography>
            {stockBajoCount > 0 && (
              <Chip
                icon={<WarningIcon sx={{ fontSize: "14px !important" }} />}
                label={`${stockBajoCount} stock bajo`}
                size="small"
                sx={{
                  bgcolor: "#fff3e0",
                  color: "#e65100",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  border: "1px solid #ffcc0260",
                  "& .MuiChip-icon": { color: "#e65100" },
                }}
              />
            )}
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button onClick={onImport} startIcon={<ImportIcon sx={{ fontSize: 16 }} />}
              sx={{ color: p.textS, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", border: `1px solid ${p.border}`, px: 2, "&:hover": { bgcolor: p.hover, borderColor: p.borderH } }}>
              Importar Excel
            </Button>
            <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={onAdd}
              sx={{ bgcolor: p.accent, color: "#FFF", px: 2.5, py: 1, fontSize: "0.84rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" } }}>
              Nuevo Inventario
            </Button>
          </Stack>
        </Box>

        <Box sx={{ px: 1, pt: 1 }}>
          <DataGrid
            rows={items} columns={columns} pageSize={10}
            rowsPerPageOptions={[5, 10, 20, 50]} pagination
            autoHeight
            checkboxSelection={false} disableSelectionOnClick loading={isLoading}
            getRowId={(row) => row.Id}
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 400, placeholder: "Buscar inventarios...", sx: { "& .MuiInputBase-root": { fontSize: "0.84rem", borderRadius: "8px", bgcolor: p.bgS, border: `1px solid ${p.border}`, px: 1.5, "&:hover": { borderColor: p.borderH }, "&.Mui-focused": { borderColor: p.accent }, "& fieldset": { border: "none" } } } },
                csvOptions: { fileName: "inventarios", delimiter: ";", utf8WithBom: true },
              }
            }}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            sx={{
              border: "none", fontSize: "0.84rem",
              "& .MuiDataGrid-columnHeaders": { bgcolor: p.bgS, borderBottom: `1px solid ${p.border}`, borderTop: `1px solid ${p.border}`, minHeight: "44px !important", maxHeight: "44px !important" },
              "& .MuiDataGrid-columnHeaderTitle": { fontSize: "0.78rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" },
              "& .MuiDataGrid-columnSeparator": { display: "none" },
              "& .MuiDataGrid-row": { borderBottom: `1px solid ${p.border}`, "&:hover": { bgcolor: p.hover }, "&:last-child": { borderBottom: "none" } },
              "& .MuiDataGrid-cell": { borderBottom: "none", py: 1 },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
              "& .MuiDataGrid-toolbarContainer": { py: 1.5, px: 2, gap: 1, "& .MuiButton-root": { fontSize: "0.78rem", color: p.textS, fontWeight: 500 } },
              "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${p.border}`, minHeight: 48 },
              "& .MuiTablePagination-root": { fontSize: "0.78rem", color: p.textS },
            }}
          />
        </Box>
      </Paper>
  );
};

export default InventariosDataTable;
